import {
  Card, Col, Empty, List, Row, Space, Statistic, Tabs, Tag, Typography,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import {
  ageingApi,
  type AgeingBucket,
  type BucketValuation,
  type DepartmentAgeingRow,
  type ItemAgeingRow,
  type MachineSpareAgeingRow,
} from '@/features/inventory/ageingApi'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import AgeingBucketBadge from './components/AgeingBucketBadge'

const { Text } = Typography

/**
 * Phase 3.2-C — Stock Ageing Dashboard.
 *
 * Everything on this page is backend-calculated. The frontend just
 * renders cards, tables, and badges. No ageing math here.
 */
export default function StockAgeingDashboardPage() {
  const summary = useQuery({
    queryKey: ['ageing-summary'],
    queryFn: () => ageingApi.summary(),
  })

  const bucket = useQuery<BucketValuation>({
    queryKey: ['ageing-bucket-val'],
    queryFn: () => ageingApi.bucketValuation(),
  })

  const items = useQuery<ItemAgeingRow[]>({
    queryKey: ['ageing-items-all'],
    queryFn: () => ageingApi.itemWise({ limit: 5000 }),
  })

  const depts = useQuery<DepartmentAgeingRow[]>({
    queryKey: ['ageing-depts'],
    queryFn: () => ageingApi.departmentWise(),
  })

  const machineRows = useQuery<MachineSpareAgeingRow[]>({
    queryKey: ['ageing-machines'],
    queryFn: () => ageingApi.machineWiseSpares(),
  })

  // Aggregate top cards from summary + bucket valuation
  const spareSummary = summary.data?.types.find((t) => t.inventory_type === 'SPARE')
  const totalValue = spareSummary?.total_stock_value ?? 0
  const above90 = (spareSummary?.value_91_180 ?? 0)
    + (spareSummary?.value_181_365 ?? 0)
    + (spareSummary?.value_365_plus ?? 0)
  const above180 = (spareSummary?.value_181_365 ?? 0)
    + (spareSummary?.value_365_plus ?? 0)
  const above365 = spareSummary?.value_365_plus ?? 0
  const slowMov = spareSummary?.slow_moving_value ?? 0
  const dead = spareSummary?.dead_stock_value ?? 0
  const critical = spareSummary?.critical_reserved_value ?? 0

  const topOldest = (items.data ?? [])
    .slice()
    .sort((a, b) => b.oldest_age_days - a.oldest_age_days)
    .slice(0, 10)
  const topValue = (items.data ?? [])
    .filter((r) => r.oldest_age_days > 180)
    .slice()
    .sort((a, b) => b.stock_value - a.stock_value)
    .slice(0, 10)
  const noIssue180 = (items.data ?? [])
    .filter((r) => r.dead_stock_flag
      || r.stock_status === 'CRITICAL_RESERVED_STOCK')
    .slice()
    .sort((a, b) => b.oldest_age_days - a.oldest_age_days)
    .slice(0, 10)

  return (
    <div>
      <PageHeader
        title="Stock Ageing Dashboard"
        subtitle="Age + value of every spare lot. Slow-moving, dead stock and critical reserved tagged automatically."
      />

      {/* Headline cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Total Inventory Value"
              value={totalValue}
              prefix="₹"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Stock > 90 days"
              value={above90}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Stock > 180 days"
              value={above180}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#fa541c' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Stock > 365 days"
              value={above365}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Slow-Moving Value"
              value={slowMov}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Dead Stock Value"
              value={dead}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6} xl={4}>
          <Card>
            <Statistic
              title="Critical Reserved Value"
              value={critical}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="buckets"
        items={[
          {
            key: 'buckets',
            label: 'Age bucket valuation',
            children: (
              <Card size="small">
                {bucket.isLoading
                  ? <Text>Loading…</Text>
                  : !bucket.data
                    ? <Empty />
                    : <DataTable
                        rowKey="bucket"
                        pagination={false}
                        columns={[
                          {
                            title: 'Ageing bucket', dataIndex: 'bucket',
                            key: 'b', width: 200,
                            render: (v: AgeingBucket) => <AgeingBucketBadge bucket={v} />,
                          },
                          { title: 'Items', dataIndex: 'items', key: 'i', width: 130 },
                          {
                            title: 'Value', dataIndex: 'value', key: 'v',
                            render: (v: number) => `₹ ${v.toFixed(2)}`,
                          },
                        ]}
                        dataSource={bucket.data?.buckets}
                      />}
              </Card>
            ),
          },
          {
            key: 'top-oldest',
            label: 'Top 10 oldest items',
            children: (
              <List
                size="small"
                dataSource={topOldest}
                renderItem={(r) => (
                  <List.Item>
                    <Space>
                      <Text strong>{r.item_code}</Text>
                      <Text type="secondary">{r.item_name}</Text>
                      <AgeingBucketBadge bucket={r.ageing_bucket}
                        status={r.stock_status} />
                      <Text>₹ {r.stock_value.toFixed(2)}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            ),
          },
          {
            key: 'top-value',
            label: 'High-value old stock',
            children: (
              <List
                size="small"
                dataSource={topValue}
                locale={{
                  emptyText: 'No items older than 180 days',
                }}
                renderItem={(r) => (
                  <List.Item>
                    <Space>
                      <Text strong>{r.item_code}</Text>
                      <Text type="secondary">{r.item_name}</Text>
                      <Tag color="volcano">{r.oldest_age_days}d</Tag>
                      <Text strong>₹ {r.stock_value.toFixed(2)}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            ),
          },
          {
            key: 'no-issue-180',
            label: 'No issue in 180 days',
            children: (
              <List
                size="small"
                dataSource={noIssue180}
                locale={{
                  emptyText: 'Every item moved in the last 180 days',
                }}
                renderItem={(r) => (
                  <List.Item>
                    <Space>
                      <Text strong>{r.item_code}</Text>
                      <Text type="secondary">{r.item_name}</Text>
                      <AgeingBucketBadge status={r.stock_status} />
                      <Text>₹ {r.stock_value.toFixed(2)}</Text>
                      {r.critical_spare_flag && <Tag color="purple">CRITICAL</Tag>}
                    </Space>
                  </List.Item>
                )}
              />
            ),
          },
          {
            key: 'dept',
            label: 'Department-wise old stock',
            children: (
              <DataTable
                rowKey="department_id"
                pagination={false}
                loading={depts.isLoading}
                dataSource={depts.data}
                columns={[
                  { title: 'Department', dataIndex: 'department_name', key: 'd' },
                  { title: 'Items', dataIndex: 'items', key: 'i', width: 100 },
                  {
                    title: 'Total Value', dataIndex: 'total_value', key: 't', width: 130,
                    render: (v: number) => `₹ ${v.toFixed(2)}`,
                  },
                  {
                    title: 'Slow-moving', dataIndex: 'slow_moving_value', key: 'sm', width: 130,
                    render: (v: number) => `₹ ${v.toFixed(2)}`,
                  },
                  {
                    title: 'Dead', dataIndex: 'dead_stock_value', key: 'ds', width: 130,
                    render: (v: number) => `₹ ${v.toFixed(2)}`,
                  },
                  {
                    title: 'Critical Reserved', dataIndex: 'critical_reserved_value', key: 'cr', width: 150,
                    render: (v: number) => `₹ ${v.toFixed(2)}`,
                  },
                ]}
              />
            ),
          },
          {
            key: 'machines',
            label: 'Machine-wise old spare value',
            children: (
              <DataTable
                rowKey={(r: MachineSpareAgeingRow) => `${r.machine_id}-${r.spare_id}`}
                pagination={false}
                loading={machineRows.isLoading}
                dataSource={machineRows.data}
                columns={[
                  { title: 'Machine', dataIndex: 'machine_code', key: 'mc', width: 130 },
                  { title: 'Spare', dataIndex: 'spare_code', key: 's', width: 130 },
                  { title: 'Name', dataIndex: 'spare_name', key: 'sn', ellipsis: true },
                  {
                    title: 'Stock', dataIndex: 'current_stock', key: 'cs', width: 90,
                    render: (v: number) => v.toFixed(2),
                  },
                  {
                    title: 'Value', dataIndex: 'balance_value', key: 'bv', width: 130,
                    render: (v: number) => `₹ ${v.toFixed(2)}`,
                  },
                  {
                    title: 'Oldest age', dataIndex: 'oldest_age_days', key: 'a',
                    width: 110, render: (v: number) => `${v}d`,
                  },
                  {
                    title: 'Used 90d / 180d', key: 'u', width: 140,
                    render: (_: unknown, r: MachineSpareAgeingRow) => (
                      <Text>{r.usage_count_90_days} / {r.usage_count_180_days}</Text>
                    ),
                  },
                  {
                    title: 'Status', dataIndex: 'stock_status', key: 'st',
                    width: 180, render: (v: ItemAgeingRow['stock_status']) =>
                      <AgeingBucketBadge status={v} />,
                  },
                ]}
              />
            ),
          },
        ]}
      />

      <Card size="small" style={{ marginTop: 12 }}>
        <Space wrap>
          <Text type="secondary">Drill down →</Text>
          <Link to="/inventory/ageing/item-wise">Item-wise</Link>
          <Link to="/inventory/ageing/lot-wise">Lot-wise</Link>
          <Link to="/inventory/ageing/slow-moving">Slow-moving</Link>
          <Link to="/inventory/ageing/dead-stock">Dead stock</Link>
          <Link to="/inventory/ageing/critical">Critical spares</Link>
          <Link to="/inventory/ageing/machine-wise">Machine-wise spares</Link>
        </Space>
      </Card>
    </div>
  )
}
